/* Single-plush pages (v3): the buy-box crew builder (snippets/plush-builder.liquid).
   Pricing mirrors the store's automatic discounts: every `bundle_min` singles take
   `bundle_off` off (up to `bundle_max_tiers` tiers), and one of every type is sold as the
   full set product instead of singles. Without this script the tiles are a one-type radio
   picker for the native product form. */
(() => {
  const money = cents => {
    const value = Math.round(cents) / 100;
    return '$' + (Number.isInteger(value) ? value : value.toFixed(2));
  };
  const list = names => (names.length <= 2 ? names.join(' and ') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`);

  function init(builder) {
    if (builder.dataset.ready) return;
    builder.dataset.ready = 'true';
    const page = builder.closest('[data-ec-page]') || document;
    const form = builder.closest('form');
    const d = builder.dataset;
    const unit = Number(d.unitCents);
    const bundleLive = d.bundleLive === 'true';
    const bundleMin = Number(d.bundleMin) || 3;
    const offCents = bundleLive ? Number(d.bundleOffCents) || 0 : 0;
    const maxTiers = Number(d.bundleMaxTiers) || 4;
    const setVariant = d.setVariantId ? Number(d.setVariantId) : null;
    const setCents = Number(d.setCents) || 0;
    const tiles = [...builder.querySelectorAll('[data-pk-tile]')];
    const setCount = Number(d.setCount) || tiles.length;
    const canSet = setVariant && setCount === tiles.length;
    const qty = new Map(tiles.map(tile => [tile, 0]));
    const hero = page.querySelector('[data-pk-hero]');
    const media = page.querySelector('[data-pk-hero-media]');
    // The sticky bar can be moved out of the page root (evolution-campaign.js): look document-wide.
    const all = selector => [...document.querySelectorAll(selector)];
    const nudge = builder.querySelector('[data-pk-nudge]');
    const nudgeText = builder.querySelector('[data-pk-nudge-text]');
    const nudgeAction = builder.querySelector('[data-pk-nudge-action]');
    let nudgeAdds = [];
    // False until the shopper taps the picker (or the sticky bar sends them to it): until then the
    // sticky bar points to the tiles instead of checking out a plush they never chose.
    let touched = false;

    builder.classList.add('pk--js');

    // Price a build exactly as checkout will: full sets first, then tiered singles.
    function quote(counts) {
      const values = tiles.map(tile => counts.get(tile));
      const total = values.reduce((sum, n) => sum + n, 0);
      const sets = canSet ? Math.min(...values) : 0;
      const singles = total - sets * tiles.length;
      const tiers = offCents ? Math.min(Math.floor(singles / bundleMin), maxTiers) : 0;
      const cents = sets * setCents + singles * unit - tiers * offCents;
      return { total, sets, singles, tiers, cents, full: total * unit };
    }

    function showHero(tile) {
      // The hero is server-rendered for the starting type: leave it alone so the browser keeps
      // the responsive image it already picked (swapping it re-downloads a 1200px file and
      // delays the largest paint).
      if (hero && tile.dataset.image && hero.dataset.variantId !== tile.dataset.variantId) {
        hero.dataset.variantId = tile.dataset.variantId;
        if (tile.dataset.imageSrcset) hero.srcset = tile.dataset.imageSrcset; else hero.removeAttribute('srcset');
        hero.src = tile.dataset.image;
        hero.width = Number(tile.dataset.imageWidth) || hero.width;
        hero.height = Number(tile.dataset.imageHeight) || hero.height;
        hero.alt = `${tile.dataset.title} plush`;
      }
      if (media) media.style.setProperty('--pk-mat', tile.dataset.mat);
      all('[data-pk-hero-type]').forEach(el => { el.textContent = tile.dataset.title; });
      all('[data-pk-hero-note]').forEach(el => { el.textContent = tile.dataset.note; });
    }

    function render() {
      const q = quote(qty);
      tiles.forEach(tile => {
        const n = qty.get(tile);
        tile.classList.toggle('is-picked', n > 0);
        const badge = tile.querySelector('[data-pk-count]');
        badge.hidden = n === 0;
        badge.textContent = `×${n}`;
        tile.querySelector('[data-pk-minus]').hidden = n === 0;
      });

      // Tier pills: highlight the reached price break.
      const pills = [...builder.querySelectorAll('[data-pk-tier]')];
      pills.forEach(li => {
        const tier = li.dataset.pkTier;
        const reached = tier === 'set' ? q.sets > 0 : !q.sets && (tier === '1' ? q.total > 0 && !q.tiers : q.tiers === Number(tier) / bundleMin);
        li.classList.toggle('is-active', reached);
      });

      // Goal meter: the bar always runs from 0 to the next break, with a tick at every break on
      // the way, so 3 shows as halfway to 6 rather than finished. Extras on top of a full set
      // count from zero.
      const count = q.sets ? q.singles : q.total;
      const goals = offCents ? Array.from({ length: maxTiers }, (_, i) => (i + 1) * bundleMin) : [];
      const next = goals.find(goal => goal > count);
      const scale = next || goals[goals.length - 1] || 1;
      const saving = q.tiers * offCents;
      const meter = builder.querySelector('[data-pk-meter]');
      const meterLabel = builder.querySelector('[data-pk-meter-label]');
      const meterCount = builder.querySelector('[data-pk-meter-count]');
      const bar = builder.querySelector('[data-pk-progress]');
      const ticks = builder.querySelector('[data-pk-ticks]');
      meter.hidden = !goals.length;
      meter.classList.toggle('is-unlocked', goals.includes(count));
      if (q.sets) {
        // A full set is in the build: credit it first, then show the extras' own ladder.
        const extras = next ? ` Add ${next - count} more ${next - count === 1 ? 'extra' : 'extras'} to save ${money(offCents)} more.` : '';
        meterLabel.textContent = `${q.sets > 1 ? `${q.sets} full sets` : 'Full set'} ✓ Saving ${money(q.full - q.cents)}.${extras}`;
        meterCount.textContent = next ? `${count}/${next} extras` : '';
        meter.classList.add('is-unlocked');
      } else if (next) {
        const more = `${next - count} more to save ${money((next / bundleMin) * offCents)}`;
        meterLabel.textContent = count === 0 ? `Pick ${next} to save ${money(offCents)}`
          : saving ? `${goals.includes(count) ? 'Unlocked' : 'Saving'} ${money(saving)}! Add ${more}` : `Add ${more}`;
        meterCount.textContent = `${count}/${next}`;
      } else {
        meterLabel.textContent = `Saving ${money(saving)}, our biggest bundle break`;
        meterCount.textContent = '';
      }
      if (bar) bar.style.width = `${Math.max(3, Math.min(1, count / scale) * 100)}%`;
      if (ticks) {
        ticks.replaceChildren(...goals.filter(goal => goal <= scale).map(goal => {
          const tick = document.createElement('span');
          tick.className = goal <= count ? 'is-hit' : '';
          tick.style.left = `${(goal / scale) * 100}%`;
          tick.textContent = goal <= count ? `✓ ${money((goal / bundleMin) * offCents)}` : `${money((goal / bundleMin) * offCents)} off`;
          return tick;
        }));
      }

      // Summary
      const title = builder.querySelector('[data-pk-sum-title]');
      const detail = builder.querySelector('[data-pk-sum-detail]');
      const picked = tiles.filter(tile => qty.get(tile) > 0);
      if (q.total === 0) { title.textContent = 'Nothing yet'; detail.textContent = 'Tap a plush to add it'; }
      else if (q.sets > 0) {
        title.textContent = q.sets > 1 ? `${q.sets} full sets` : 'The full set';
        detail.textContent = `${q.singles ? `+ ${q.singles} extra ${q.singles === 1 ? 'plush' : 'plushes'}` : `All ${tiles.length}, one of each`} · saving ${money(q.full - q.cents)}`;
      } else if (q.total === 1) { title.textContent = picked[0].dataset.title; detail.textContent = 'One plush'; }
      else { title.textContent = `${q.total} plushes`; detail.textContent = q.tiers ? `Bundle price · saving ${money(q.full - q.cents)}` : offCents ? `Add ${bundleMin - q.total} more to save ${money(offCents)}` : 'Mix of your favorites'; }
      const allOneLabel = builder.querySelector('[data-pk-all-one-label]');
      if (allOneLabel) allOneLabel.textContent = q.sets ? 'Add another full set' : 'Add 1 of each';
      all('[data-pk-count-label]').forEach(el => { el.textContent = q.total ? `${q.total} picked` : 'Tap to add'; });
      const was = builder.querySelector('[data-pk-sum-was]');
      was.hidden = q.cents >= q.full;
      was.textContent = money(q.full);
      builder.querySelector('[data-pk-sum-total]').textContent = money(q.cents);
      all('[data-pk-total]').forEach(el => { el.textContent = money(q.cents); });

      // Nudge the shopper up: complete the set when close, otherwise reach the next tier.
      nudgeAdds = [];
      let message = '';
      let shortHint = '';
      const missing = tiles.filter(tile => qty.get(tile) === q.sets && tile.dataset.available === 'true');
      if (canSet && q.total > 0 && missing.length > 0 && missing.length <= 3) {
        const next = new Map(qty);
        missing.forEach(tile => next.set(tile, next.get(tile) + 1));
        const after = quote(next);
        const delta = after.cents - q.cents;
        const names = list(missing.map(tile => tile.dataset.title));
        // Spell out how much more the set saves than the bundle breaks.
        const nowSaving = q.full - q.cents;
        const setSaving = money(after.full - after.cents);
        const compare = `the full set saves ${setSaving}${nowSaving > 0 ? `, not ${money(nowSaving)}` : ''}`;
        message = delta <= 0
          ? `Add ${names} and pay ${money(-delta)} less: ${compare}.`
          : `Add ${names} for ${money(delta)} more: ${compare}.`;
        shortHint = `${delta <= 0 ? `Pay ${money(-delta)} less` : `${money(delta)} more`} for the full set: save ${setSaving}`;
        nudgeAdds = missing;
        nudgeAction.textContent = missing.length === 1 ? `Add ${missing[0].dataset.title}` : `Add the missing ${missing.length}`;
      }
      // Test 'add3' (snippets/zk-split-test.liquid), version B: one short of the first bundle break, offer
      // the next plush with one tap and say what it costs.
      if (!message && window.zkTests && window.zkTests.add3 === 'B' && offCents && !q.sets && q.total === bundleMin - 1) {
        const pick = tiles.find(tile => qty.get(tile) === 0 && tile.dataset.available === 'true');
        if (pick) {
          const next = new Map(qty);
          next.set(pick, 1);
          const after = quote(next);
          const nth = { 2: '2nd', 3: '3rd' }[bundleMin] || `${bundleMin}th`;
          message = `Add a ${nth} for just ${money(after.cents - q.cents)}. ${bundleMin} for ${money(after.cents)}.`;
          shortHint = `Add a ${nth} for ${money(after.cents - q.cents)}`;
          nudgeAdds = [pick];
          nudgeAction.textContent = `Add ${pick.dataset.title}`;
        }
      }
      nudge.hidden = !message;
      // One savings message at a time: the set nudge replaces the meter while it shows.
      if (message) meter.hidden = true;
      nudgeText.textContent = message;
      all('[data-pk-hint]').forEach(el => { el.textContent = message ? shortHint : (meter.hidden ? `${q.total} picked · ships free` : meterLabel.textContent); });
      nudgeAction.hidden = nudgeAdds.length === 0;

      // The form goes straight to checkout, so the buttons say so.
      all('[data-pk-cta]').forEach(button => {
        button.disabled = q.total === 0;
        const label = button.querySelector('[data-pk-cta-label]') || button;
        const choose = Boolean(button.dataset.short) && !touched;
        label.textContent = q.total === 0 ? 'Pick a plush'
          : choose ? 'Choose yours ↑'
          : button.dataset.short ? `Checkout · ${money(q.cents)}` : `Checkout — ${money(q.cents)}`;
      });
    }

    function change(tile, delta) {
      if (delta > 0 && tile.dataset.available !== 'true') return;
      touched = true;
      qty.set(tile, Math.max(0, Math.min(20, qty.get(tile) + delta)));
      if (delta > 0) showHero(tile);
      const radio = tile.querySelector('.pk-tile-input');
      if (radio && qty.get(tile) > 0) radio.checked = true;
      render();
    }

    tiles.forEach(tile => {
      tile.querySelector('[data-pk-add]').addEventListener('click', event => { event.preventDefault(); change(tile, 1); });
      tile.querySelector('[data-pk-minus]').addEventListener('click', () => change(tile, -1));
    });
    // Quick add: fill in the missing types so the build holds one more full set. Types already
    // picked are topped up, not added on top (a Dark ×1 build becomes exactly the set).
    function addSet() {
      touched = true;
      const target = quote(qty).sets + 1;
      tiles.forEach(tile => { if (tile.dataset.available === 'true') qty.set(tile, Math.min(20, Math.max(qty.get(tile), target))); });
      render();
    }
    builder.querySelector('[data-pk-all-one]')?.addEventListener('click', addSet);
    nudgeAction.addEventListener('click', () => {
      touched = true;
      nudgeAdds.forEach(tile => qty.set(tile, qty.get(tile) + 1));
      if (nudgeAdds.length) showHero(nudgeAdds[nudgeAdds.length - 1]);
      render();
    });

    // Tier bar: shoppers tap the price breaks, so they work. "1 of each" fills in the full set (once);
    // "Just 1" / "Any 3" / "Any 6" take them to the tiles, which pulse to show where to tap.
    const build = builder.querySelector('.pk-build');
    function guide() {
      build?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      builder.classList.remove('pk-guide');
      void builder.offsetWidth;
      builder.classList.add('pk-guide');
      setTimeout(() => builder.classList.remove('pk-guide'), 1800);
    }
    builder.querySelectorAll('[data-pk-tier]').forEach(li => {
      const tier = li.dataset.pkTier;
      if (tier === 'set' && !canSet) return;
      li.setAttribute('role', 'button');
      li.tabIndex = 0;
      li.setAttribute('aria-label', tier === 'set' ? `Add 1 of each, the full set of ${setCount}` : tier === '1' ? 'Pick one plush' : `Pick any ${tier}`);
      const pick = () => {
        window.clarity?.('event', 'zk_tier_tap_' + tier);
        if (tier === 'set') { if (!quote(qty).sets) addSet(); return; }
        guide(); // only shows where to pick; the sticky bar keeps saying "Choose yours" until they do
      };
      li.addEventListener('click', pick);
      li.addEventListener('keydown', event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); pick(); } });
    });

    // Start with the variant from the URL (or the first available one) picked once.
    const start = tiles.find(tile => tile.querySelector('.pk-tile-input')?.checked && tile.dataset.available === 'true')
      || tiles.find(tile => tile.dataset.available === 'true');
    if (start) { qty.set(start, 1); showHero(start); }
    render();

    // Sticky bar before any pick: scroll to the tiles instead of submitting.
    all('[data-pk-cta][data-short]').forEach(button => button.addEventListener('click', event => {
      if (touched) return;
      event.preventDefault();
      touched = true;
      builder.querySelector('.pk-build')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      render();
    }));

    if (!form) return;
    form.addEventListener('submit', async event => {
      event.preventDefault();
      const q = quote(qty);
      if (!q.total) return;
      const properties = {};
      new FormData(form).forEach((value, key) => {
        const match = key.match(/^properties\[(.+)\]$/);
        if (match) properties[match[1]] = value;
      });
      const items = [];
      if (q.sets) items.push({ id: setVariant, quantity: q.sets, properties: { ...properties, _zk_builder: 'set' } });
      tiles.forEach(tile => {
        const extra = qty.get(tile) - q.sets;
        if (extra > 0) items.push({ id: Number(tile.dataset.variantId), quantity: extra, properties: { ...properties, _zk_builder: 'single' } });
      });
      all('[data-pk-cta]').forEach(button => { button.disabled = true; });
      const label = event.submitter && event.submitter.querySelector('[data-pk-cta-label]');
      if (label) label.textContent = 'Opening checkout…';
      try {
        // Empty the cart first: backing out of checkout and tapping again would otherwise check out
        // everything twice, with no cart screen in between to fix it.
        await fetch('/cart/clear.js', { method: 'POST', headers: { Accept: 'application/json' } }).catch(() => {});
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });
        if (!response.ok) throw new Error((await response.json().catch(() => ({}))).description || 'Could not add to bag');
        // Give the ad pixels (Meta, TikTok) a moment to send add-to-cart before leaving the page.
        await new Promise(resolve => setTimeout(resolve, 600));
        window.location.href = '/checkout';
      } catch (error) {
        render();
        nudge.hidden = false;
        nudgeText.textContent = `${error.message}. Please try again.`;
        nudgeAction.hidden = true;
      }
    });
    window.addEventListener('pageshow', render);
  }

  const run = scope => scope.querySelectorAll('[data-pk-builder]').forEach(init);
  run(document);
  document.addEventListener('shopify:section:load', event => run(event.target));
})();
